import React from 'react';
import type { IntensityTarget, SportType } from '@/types';
import { INTENSITY_TARGETS_BY_SPORT } from '@/types';
import type { TranslationKeys } from '@/core/i18n/translations';

interface IntensityTargetSelectorProps {
  sport: SportType;
  /** The intensity target currently in effect for this sport (bike: power|hr; run: pace|hr|power). */
  value: IntensityTarget;
  /** Fires only for choices valid for the current sport. */
  onChange: (next: IntensityTarget) => void;
  t: TranslationKeys;
}

/**
 * Segmented control for the "Docelowa Intensywność" choice. Renders 2 buttons for cycling
 * (Moc / Tętno) and 3 for running (Tempo / Tętno / Moc). Designed to live above the zone inputs
 * so the user sees, in order: sport tab → intensity target → unit label → editable S1-S7 inputs.
 */
export function IntensityTargetSelector({ sport, value, onChange, t }: IntensityTargetSelectorProps) {
  const available = INTENSITY_TARGETS_BY_SPORT[sport];

  const labelFor = (target: IntensityTarget): string => {
    if (target === 'pace')  return t.intensityTargetPace;
    if (target === 'hr')    return t.intensityTargetHr;
    return t.intensityTargetPower;
  };

  return (
    <div className="mb-4">
      <span className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
        {t.intensityTargetLabel}
      </span>
      <div
        role="tablist"
        aria-label={t.intensityTargetLabel}
        className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-0.5"
        data-testid="intensity-target-selector"
      >
        {available.map((target) => {
          const selected = target === value;
          return (
            <button
              key={target}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onChange(target)}
              data-testid={`intensity-target-${target}`}
              className={`min-h-[36px] px-3 text-xs font-semibold rounded-md transition-colors ${
                selected
                  ? 'bg-white dark:bg-slate-800 text-orange-700 shadow-sm border border-slate-200 dark:border-slate-700'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
              }`}
            >
              {labelFor(target)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
